import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    AiOutlineBell,
    AiOutlineClose,
    AiOutlineShopping,
    AiOutlineDollar,
    AiOutlineExclamationCircle,
    AiOutlineUser,
    AiOutlineSetting,
    AiOutlineInfoCircle,
    AiOutlineMail,
} from 'react-icons/ai';
import myAxios from '../AxiosUrl';
import { WS_URL } from '../AxiosUrlGlobal';
import { useFCM } from '../../firebase/useFCM';
import { useWebSocket } from '../../hooks/useWebSocket';
import styles from './HeaderNotification.module.css';

/** Notification ids already alerted in this tab (FCM foreground + WebSocket can both deliver one). */
const shownIds = () => (window.__etechShownNotifs = window.__etechShownNotifs || new Set());

/** Where a click on a notification goes (owner 2026-11-04: clicking did nothing). */
export function notificationLink(n) {
    const d = (n && n.data) || {};
    const url = d.url || d.link;
    if (typeof url === 'string' && url.startsWith('/')) return url;
    if (d.callId) return `/ai-calling?tab=calls&call=${encodeURIComponent(d.callId)}`;
    if (String(d.kind || '').startsWith('ai_call')) return '/ai-calling';
    if (n.type === 'email') return '/email-inbox';
    if (n.type === 'inventory') return '/product';
    if (n.type === 'order') return '/order-list';
    return null;
}

function HeaderNotification() {
    const navigate = useNavigate();
    const uid = localStorage.getItem('id');
    const token = localStorage.getItem('token');

    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // Firebase FCM for push notifications
    const { fcmToken, notification: fcmNotification, isPermissionGranted } = useFCM();

    // WebSocket for real-time updates — always on: some notifications (new email, AI calling) are
    // pushed over the socket, and an open tab gets them instantly even when FCM push is allowed.
    const { isConnected: wsConnected, notifications: wsNotifications } = useWebSocket(WS_URL, token, true);

    /**
     * Fetch notifications from backend
     */
    const fetchNotifications = useCallback(async () => {
        if (!uid || !token) return;

        try {
            setLoading(true);
            const response = await myAxios.get(`/notifications/${uid}`, {
                headers: { token },
            });

            if (response.data) {
                setNotifications(response.data.data || []);
                setUnreadCount(response.data.unreadCount || 0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [uid, token]);

    /**
     * Mark single notification as read
     */
    const markAsRead = async (notificationId) => {
        if (!token) return;

        try {
            await myAxios.post(
                `/notification-read/${notificationId}`,
                {},
                {
                    headers: { token },
                }
            );

            // Update local state
            setNotifications((prev) =>
                prev.map((notification) =>
                    notification._id === notificationId
                        ? { ...notification, status: 'read' }
                        : notification
                )
            );

            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    /**
     * Mark all notifications as read
     */
    const markAllAsRead = async () => {
        if (!uid || !token) return;

        try {
            await myAxios.post(
                '/notifications-read-all',
                { uid },
                {
                    headers: { token },
                }
            );

            // Update local state
            setNotifications((prev) =>
                prev.map((notification) => ({ ...notification, status: 'read' }))
            );

            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    /**
     * Alert once per notification: an in-page toast, or a system notification while the tab is hidden.
     */
    const showToast = (id, title, message, link) => {
        const seen = shownIds();
        if (id && seen.has(String(id))) return;
        if (id) seen.add(String(id));
        if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
            const sys = new Notification(title, { body: message, icon: '/logo192.png', badge: '/logo192.png', tag: id ? String(id) : undefined });
            sys.onclick = () => {
                window.focus();
                if (link) navigate(link);
                sys.close();
            };
            return;
        }
        toast.info(`${title}${message ? `: ${message}` : ''}`, { position: 'top-right', autoClose: 6000, onClick: () => link && navigate(link) });
    };

    /**
     * Initial fetch on component mount
     */
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    /**
     * Safety net: refresh the list every minute while the tab is visible, and when it comes back.
     */
    useEffect(() => {
        const tick = () => !document.hidden && fetchNotifications();
        const t = setInterval(tick, 60000);
        document.addEventListener('visibilitychange', tick);
        return () => {
            clearInterval(t);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [fetchNotifications]);

    /**
     * Handle WebSocket notifications (real-time)
     */
    useEffect(() => {
        if (wsNotifications.length > 0) {
            const latestNotification = wsNotifications[0];

            // Add to list and count it — once
            setNotifications((prev) => {
                if (prev.some((n) => String(n._id) === String(latestNotification._id))) return prev;
                if (latestNotification.status !== 'read') setUnreadCount((c) => c + 1);
                return [latestNotification, ...prev];
            });

            showToast(latestNotification._id, latestNotification.title, latestNotification.message, notificationLink(latestNotification));
            // let open pages react (the email inbox refreshes on a new email)
            window.dispatchEvent(new CustomEvent('etech-notification', { detail: latestNotification }));
        }
    }, [wsNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Handle FCM foreground notifications
     */
    useEffect(() => {
        if (fcmNotification) {
            // Refresh notifications from server (useFCM already showed its toast)
            fetchNotifications();
            const id = fcmNotification.data?.notificationId;
            if (id) shownIds().add(String(id));
            window.dispatchEvent(new CustomEvent('etech-notification', { detail: { _id: id, type: fcmNotification.data?.type, data: fcmNotification.data } }));
        }
    }, [fcmNotification]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Refresh notifications when dropdown opens
     */
    useEffect(() => {
        if (isNotificationOpen) {
            fetchNotifications();
        }
    }, [isNotificationOpen, fetchNotifications]);

    /** Click: mark read, then open the page it is about. */
    const openNotification = (notification) => {
        if (notification.status === 'unread') markAsRead(notification._id);
        const link = notificationLink(notification);
        if (link) {
            setIsNotificationOpen(false);
            navigate(link);
        }
    };

    /**
     * Format time ago (e.g., "2h ago")
     */
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

        if (diffInHours < 1) {
            return 'Just now';
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays}d ago`;
        }
    };

    /**
     * Get notification icon based on type
     */
    const getNotificationIcon = (type) => {
        const iconMap = {
            order: AiOutlineShopping,
            payment: AiOutlineDollar,
            inventory: AiOutlineExclamationCircle,
            review: AiOutlineUser,
            system: AiOutlineSetting,
            domain: AiOutlineInfoCircle,
            email: AiOutlineMail,
        };
        return iconMap[type] || AiOutlineBell;
    };

    /**
     * Get priority color class
     */
    const getPriorityColor = (priority) => {
        const colorMap = {
            high: styles.priorityHigh,
            normal: styles.priorityNormal,
            low: styles.priorityLow,
        };
        return colorMap[priority] || styles.priorityNormal;
    };

    return (
        <div className={styles.notificationContainer}>
            {/* Notification Button */}
            <button
                className={`${styles.notificationButton} ${
                    isNotificationOpen ? styles.active : ''
                }`}
                onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                title="Notifications"
            >
                <AiOutlineBell className={styles.notificationIcon} />
                {unreadCount > 0 && (
                    <span className={styles.unreadBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {/* Notification Dropdown */}
            {isNotificationOpen && (
                <div className={styles.notificationDropdown}>
                    <div className={styles.notificationHeader}>
                        <h3 className={styles.notificationTitle}>Notifications</h3>
                        <div className={styles.notificationActions}>
                            {unreadCount > 0 && (
                                <button
                                    className={styles.markAllButton}
                                    onClick={markAllAsRead}
                                >
                                    Mark all as read
                                </button>
                            )}
                            <button
                                className={styles.closeButton}
                                onClick={() => setIsNotificationOpen(false)}
                            >
                                <AiOutlineClose />
                            </button>
                        </div>
                    </div>

                    {/* Connection Status Indicator */}
                    <div className={styles.connectionStatus}>
                        {isPermissionGranted && fcmToken && (
                            <div className={styles.statusBadge}>
                                <span className={styles.statusDot} style={{ backgroundColor: '#10b981' }}></span>
                                <span>Push notifications enabled</span>
                            </div>
                        )}
                        {!isPermissionGranted && wsConnected && (
                            <div className={styles.statusBadge}>
                                <span className={styles.statusDot} style={{ backgroundColor: '#3b82f6' }}></span>
                                <span>Live updates active</span>
                            </div>
                        )}
                        {!isPermissionGranted && !wsConnected && (
                            <div className={styles.statusBadge}>
                                <span className={styles.statusDot} style={{ backgroundColor: '#f59e0b' }}></span>
                                <span>Using periodic refresh</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.notificationContent}>
                        <div className={styles.notificationStats}>
                            <span className={styles.notificationCount}>
                                {notifications.length} notification
                                {notifications.length !== 1 ? 's' : ''}
                            </span>
                            {unreadCount > 0 && (
                                <span className={styles.unreadCount}>
                                    {unreadCount} unread
                                </span>
                            )}
                        </div>

                        <div className={styles.notificationsList}>
                            {loading ? (
                                <div className={styles.loadingState}>
                                    <div className={styles.loader}></div>
                                    <p>Loading notifications...</p>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIcon}>
                                        <AiOutlineBell />
                                    </div>
                                    <h3 className={styles.emptyTitle}>No notifications</h3>
                                    <p className={styles.emptyDescription}>
                                        You're all caught up!
                                    </p>
                                </div>
                            ) : (
                                notifications.map((notification) => {
                                    const IconComponent = getNotificationIcon(
                                        notification.type
                                    );
                                    return (
                                        <div
                                            key={notification._id}
                                            className={`${styles.notificationItem} ${
                                                notification.status === 'unread'
                                                    ? styles.unread
                                                    : ''
                                            }`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openNotification(notification)}
                                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), openNotification(notification))}
                                        >
                                            <div className={styles.itemIcon}>
                                                <IconComponent className={styles.icon} />
                                            </div>
                                            <div className={styles.itemBody}>
                                                <div className={styles.itemHead}>
                                                    <h4 className={styles.itemTitle}>
                                                        {notification.title}
                                                    </h4>
                                                    <div className={styles.notificationMeta}>
                                                        <span
                                                            className={
                                                                styles.notificationTime
                                                            }
                                                        >
                                                            {formatTime(
                                                                notification.createdAt
                                                            )}
                                                        </span>
                                                        <span
                                                            className={`${
                                                                styles.priorityBadge
                                                            } ${getPriorityColor(
                                                                notification.priority
                                                            )}`}
                                                        >
                                                            {notification.priority}
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className={styles.notificationMessage}>
                                                    {notification.message}
                                                </p>
                                                <div className={styles.notificationFooter}>
                                                    <span
                                                        className={styles.notificationType}
                                                    >
                                                        {notification.type}
                                                    </span>
                                                    {notificationLink(notification) && <span className={styles.itemLink}>Open →</span>}
                                                    {notification.status === 'unread' && (
                                                        <div
                                                            className={
                                                                styles.unreadIndicator
                                                            }
                                                        ></div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay for mobile */}
            {isNotificationOpen && (
                <div
                    className={styles.overlay}
                    onClick={() => setIsNotificationOpen(false)}
                />
            )}
        </div>
    );
}

export default HeaderNotification;
